import React from 'react';
import { Project, Company, User, PieChartSegment, ExpenseEntry, ReportCategoryGroup } from '../types';
import { Logo } from './Logo';
import { PieChartDisplay } from './PieChartDisplay';
import { formatCurrency, formatDate } from '../constants';
// (Imported from types.ts)

interface PrintableReportProps {
  project: Project;
  company: Company;
  user: User;
  reportData: ReportCategoryGroup[];
  pieChartData: PieChartSegment[];
  grandTotal: number;
  reportPeriod: { month: string; year: string } | null;
  version?: string;
  isDetailedReport?: boolean;
  detailedExpenses?: ExpenseEntry[];
  detailedReportTitle?: string;
  showChart?: boolean;
}

const DetailedReportView: React.FC<Pick<PrintableReportProps, 'detailedExpenses' | 'detailedReportTitle' | 'grandTotal'>> = ({
    detailedExpenses = [],
    detailedReportTitle,
    grandTotal,
}) => (
    <section>
        <h2 className="font-bold mb-2" style={{ fontSize: '11pt' }}>
            {detailedReportTitle || 'Lançamentos Detalhados'}
        </h2>
        <table className="w-full border-collapse" style={{ fontSize: '9pt' }}>
            <thead>
                <tr className="border-b border-black">
                    <th className="py-1 px-1 text-left font-bold w-20">Data</th>
                    <th className="py-1 px-1 text-left font-bold">Fornecedor</th>
                    <th className="py-1 px-1 text-left font-bold w-24">Nota Fiscal</th>
                    <th className="py-1 px-1 text-left font-bold">Descrição</th>
                    <th className="py-1 px-1 text-right font-bold w-32">Valor</th>
                </tr>
            </thead>
            <tbody>
                {detailedExpenses.map((exp) => (
                    <tr key={exp.id} className="border-b border-gray-300">
                        <td className="py-1 px-1">{formatDate(exp.date, exp.month)}</td>
                        <td className="py-1 px-1">{exp.supplier || '-'}</td>
                        <td className="py-1 px-1">{exp.invoice || '-'}</td>
                        <td className="py-1 px-1">{exp.description || '-'}</td>
                        <td className="py-1 px-1 text-right">{formatCurrency(exp.amount)}</td>
                    </tr>
                ))}
            </tbody>
            <tfoot>
                <tr className="font-bold bg-gray-200">
                    <td colSpan={4} className="py-1 px-1 text-right">Total</td>
                    <td className="py-1 px-1 text-right">{formatCurrency(grandTotal)}</td>
                </tr>
            </tfoot>
        </table>
    </section>
);


const SummaryReportView: React.FC<Pick<PrintableReportProps, 'reportData' | 'pieChartData' | 'grandTotal' | 'reportPeriod' | 'showChart'>> = ({
    reportData,
    pieChartData,
    grandTotal,
    reportPeriod,
    showChart = true,
}) => (
    <>
        {/* Grand Total */}
        <section className="bg-gray-200 flex justify-between items-center px-2 py-1 font-bold" style={{ fontSize: '11pt' }}>
            <span>Custo Geral</span>
            <span>{formatCurrency(grandTotal)}</span>
        </section>

        {/* Pie Chart */}
        {showChart && pieChartData.length > 0 && (
            <section id="report-pie-chart-for-pdf">
                <h2 className="font-bold" style={{ fontSize: '10pt' }}>Resumo por Categoria – {reportPeriod?.month}/{reportPeriod?.year}</h2>
                <div className="flex justify-center py-1">
                    <div className="border border-gray-400 p-1 inline-block">
                        <PieChartDisplay data={pieChartData} layout="horizontal" size={60} />
                    </div>
                </div>
            </section>
        )}
        
        {/* Cost Tables */}
        <div className="space-y-1">
            {reportData.map(category => (
                <section key={category.id}>
                    <h2 className="font-bold" style={{ fontSize: '11pt' }}>{category.name}</h2>
                    <table className="w-full border-collapse" style={{ fontSize: '9pt' }}>
                        <thead>
                            <tr className="border-b border-black">
                                <th className="py-1 px-1 text-left font-bold">Descrição</th>
                                <th className="py-1 px-1 text-right font-bold w-40">Valor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {category.subItems.map((item) => (
                                <tr key={item.name} className="border-b border-gray-300">
                                    <td className="py-1 px-1">{item.name}</td>
                                    <td className="py-1 px-1 text-right">{formatCurrency(item.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="font-bold bg-gray-200">
                                <td className="py-1 px-1 text-right">Total</td>
                                <td className="py-1 px-1 text-right">
                                    <div className="flex justify-end items-baseline gap-x-3">
                                        {grandTotal > 0 && (
                                            <span className="font-normal text-gray-700" style={{ fontSize: '8pt' }}>
                                            ({((category.total / grandTotal) * 100).toFixed(2).replace('.',',')}%)
                                            </span>
                                        )}
                                        <span>{formatCurrency(category.total)}</span>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </section>
            ))}
        </div>
    </>
);


export const PrintableReport: React.FC<PrintableReportProps> = ({
  project,
  company,
  user,
  reportData,
  pieChartData,
  grandTotal,
  reportPeriod,
  version,
  isDetailedReport,
  detailedExpenses,
  detailedReportTitle,
  showChart = true,
}) => {
  const today = new Date();

  return (
    <div id="report-content" className="bg-white text-black font-sans mx-auto" style={{ width: '210mm', padding: '10mm 15mm' }}>
      {/* Header */}
      <header className="flex justify-between items-center pb-1 mb-1 border-b-2 border-black">
        <div className="h-10">
          <Logo isSidebarOpen={true} />
        </div>
        <h1 className="font-bold text-black" style={{ fontSize: '14pt' }}>Relatório de Custos – {reportPeriod ? `${reportPeriod.month} ${reportPeriod.year}` : ''}</h1>
      </header>

      <div className="space-y-1" style={{ fontSize: '9pt' }}>
        {/* Client Info */}
        <section className="mb-1">
          <h2 className="font-bold border-b border-black pb-1" style={{ fontSize: '11pt' }}>Dados do cliente</h2>
          <div className="grid grid-cols-2 gap-x-4 pt-1">
            <div>
              <p><strong>Cliente:</strong> {company.name}</p>
              <p><strong>CNPJ:</strong> {company.cnpj}</p>
              <p><strong>Encarregado:</strong> {project.personInCharge}</p>
            </div>
            <div>
              <p><strong>Endereço:</strong> {`${company.address.street}, ${company.address.number || 'S/N'}`}</p>
              <p><strong>Cidade:</strong> {`${company.address.city}/${company.address.state}`}</p>
            </div>
          </div>
        </section>
        
        {isDetailedReport ? (
            <DetailedReportView 
                detailedExpenses={detailedExpenses}
                detailedReportTitle={detailedReportTitle}
                grandTotal={grandTotal}
            />
        ) : (
            <SummaryReportView 
                reportData={reportData}
                pieChartData={pieChartData}
                grandTotal={grandTotal}
                reportPeriod={reportPeriod}
                showChart={showChart}
            />
        )}
      </div>

      {/* Footer */}
      <footer className="flex justify-between items-center text-gray-600 mt-2 pt-1 border-t border-gray-400" style={{ fontSize: '7pt' }}>
        {version && <span>{version}</span>}
        <span className="text-right flex-grow">Relatório gerado por {user.name} em {today.toLocaleString('pt-BR')}</span>
      </footer>
    </div>
  );
};
